import { GetStaticProps } from "next"
import { useContext } from "react"
import {
	Box,
	Container,
	Grid,
	Paper,
	Hidden,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Typography,
} from "@mui/material"
import Icon from "@mdi/react"
import { mdiLaptop, mdiTruck, mdiCog, mdiTools, mdiAlert } from "@mdi/js"
import { MdOutlineExpandMore } from "react-icons/md"

import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"

import useTranslation from "hooks/useTranslation"
import AppContext from "@secureo/common/context/AppContext/AppContext"

import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { makeStyles } from "makeStyles"
import { helpCenterBreadCrumb } from "./index"
import {
	leftNavs,
	websiteFAQs,
	deliveryFAQs,
	technicalQuestionsFAQs,
	assemblyFAQs,
	problemFAQs,
} from "utils/faq/help-center-faq"

const useStyles = makeStyles()((theme) => ({
	paper: {
		padding: theme.spacing(2),
		color: "#274041",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		marginBottom: "20px",
	},
	root: {
		width: "100%",
	},
	heading: {
		fontSize: theme.typography.pxToRem(15),
		flexBasis: "95%",
		flexShrink: 0,
	},
}))

const FAQs = () => {
	const { classes } = useStyles()
	const { language } = useContext(AppContext)
	const { t } = useTranslation("help")

	const breadCrumb: BreadCrumbAncestor = {
		label: "FAQ",
		href: "/help/faq",
	}

	return (
		<Box my={5}>
			<Box style={{ marginTop: "-40px", paddingBottom: "20px" }}>
				<BreadCrumbs
					breadCrumbsPaths={[
						{ ...helpCenterBreadCrumb, label: t(helpCenterBreadCrumb.label) },
					]}
					currentPath={breadCrumb}
				/>
			</Box>
			<Container>
				<Box pb={5} style={{ textAlign: "center", maxWidth: "650px", margin: "auto" }}>
					<h1>FAQs</h1>
					<p style={{ color: "grey" }}>{t("faqText")}</p>
				</Box>
				<Grid container spacing={3}>
					<Hidden mdDown>
						<Grid item xs={3}>
							{leftNavs.map(
								({ titleByLanguage, descriptionByLanguage, link, icon }) => {
									const title = titleByLanguage[language] ?? titleByLanguage.de
									const description =
										descriptionByLanguage[language] ?? descriptionByLanguage.de
									return (
										<a
											href={`#${link}`}
											style={{ textDecoration: "none" }}
											key={title}
										>
											<Paper className={classes.paper}>
												<div style={{ display: "flex" }}>
													<div style={{ marginRight: "15px" }}>
														{icon}
													</div>
													<div>
														<strong>{title}</strong> <br />
														{description}
													</div>
												</div>
											</Paper>
										</a>
									)
								},
							)}
						</Grid>
					</Hidden>
					<Grid item xs={12} md={9}>
						<Paper className={classes.paper} id="website">
							<div style={{ display: "flex" }}>
								<div style={{ marginRight: "15px" }}>
									<Icon path={mdiLaptop} size={1.5} color="#7ABFAC" />
								</div>
								<div>
									<strong>Website</strong> <br />
									{t("websiteText")}
								</div>
							</div>
							<Box mt={3}>
								{websiteFAQs.map(({ questionByLanguage, answerByLanguage }) => {
									const question =
										questionByLanguage[language] ?? questionByLanguage.de
									const answer = answerByLanguage[language] ?? answerByLanguage.de
									return (
										<Accordion key={question}>
											<AccordionSummary
												expandIcon={<MdOutlineExpandMore size="1.2rem" />}
												aria-controls="panel1a-content"
												id="panel1a-header"
											>
												<Typography className={classes.heading}>
													{question}
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Typography>{answer}</Typography>
											</AccordionDetails>
										</Accordion>
									)
								})}
							</Box>
						</Paper>
						<Paper className={classes.paper} id="delivery">
							<div style={{ display: "flex" }}>
								<div style={{ marginRight: "15px" }}>
									<Icon path={mdiTruck} size={1.5} color="#FFD462" />
								</div>
								<div>
									<strong>{t("delivery")}</strong> <br />
									{t("deliveryText")}
								</div>
							</div>
							<Box mt={3}>
								{deliveryFAQs.map(({ questionByLanguage, answerByLanguage }) => {
									const question =
										questionByLanguage[language] ?? questionByLanguage.de
									const answer = answerByLanguage[language] ?? answerByLanguage.de
									return (
										<Accordion key={question}>
											<AccordionSummary
												expandIcon={<MdOutlineExpandMore size="1.2rem" />}
												aria-controls="panel1a-content"
												id="panel1a-header"
											>
												<Typography className={classes.heading}>
													{question}
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Typography>{answer}</Typography>
											</AccordionDetails>
										</Accordion>
									)
								})}
							</Box>
						</Paper>
						<Paper className={classes.paper} id="technical-questions">
							<div style={{ display: "flex" }}>
								<div style={{ marginRight: "15px" }}>
									<Icon path={mdiCog} size={1.5} color="#1B5C71" />
								</div>
								<div>
									<strong>{t("technicalQuestions")}</strong> <br />
									{t("technicalQuestionsText")}
								</div>
							</div>
							<Box mt={3}>
								{technicalQuestionsFAQs.map(
									({ questionByLanguage, answerByLanguage }) => {
										const question =
											questionByLanguage[language] ?? questionByLanguage.de
										const answer =
											answerByLanguage[language] ?? answerByLanguage.de
										return (
											<Accordion key={question}>
												<AccordionSummary
													expandIcon={
														<MdOutlineExpandMore size="1.2rem" />
													}
													aria-controls="panel1a-content"
													id="panel1a-header"
												>
													<Typography className={classes.heading}>
														{question}
													</Typography>
												</AccordionSummary>
												<AccordionDetails>
													<Typography>{answer}</Typography>
												</AccordionDetails>
											</Accordion>
										)
									},
								)}
							</Box>
						</Paper>
						<Paper className={classes.paper} id="assembly">
							<div style={{ display: "flex" }}>
								<div style={{ marginRight: "15px" }}>
									<Icon path={mdiTools} size={1.5} color="#418389" />
								</div>
								<div>
									<strong>{t("assembly")}</strong> <br />
									{t("assemblyText")}
								</div>
							</div>
							<Box mt={3}>
								{assemblyFAQs.map(({ questionByLanguage, answerByLanguage }) => {
									const question =
										questionByLanguage[language] ?? questionByLanguage.de
									const answer = answerByLanguage[language] ?? answerByLanguage.de
									return (
										<Accordion key={question}>
											<AccordionSummary
												expandIcon={<MdOutlineExpandMore size="1.2rem" />}
												aria-controls="panel1a-content"
												id="panel1a-header"
											>
												<Typography className={classes.heading}>
													{question}
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Typography>{answer}</Typography>
											</AccordionDetails>
										</Accordion>
									)
								})}
							</Box>
						</Paper>
						<Paper className={classes.paper} id="problems">
							<div style={{ display: "flex" }}>
								<div style={{ marginRight: "15px" }}>
									<Icon path={mdiAlert} size={1.5} color="#CF423B" />
								</div>
								<div>
									<strong>{t("problems")}</strong> <br />
									{t("problemsText")}
								</div>
							</div>
							<Box mt={3}>
								{problemFAQs.map(({ questionByLanguage, answerByLanguage }) => {
									const question =
										questionByLanguage[language] ?? questionByLanguage.de
									const answer = answerByLanguage[language] ?? answerByLanguage.de

									return (
										<Accordion key={question}>
											<AccordionSummary
												expandIcon={<MdOutlineExpandMore size="1.2rem" />}
												aria-controls="panel1a-content"
												id="panel1a-header"
											>
												<Typography className={classes.heading}>
													{question}
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Typography>{answer}</Typography>
											</AccordionDetails>
										</Accordion>
									)
								})}
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</Box>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const props = {
		...(await getServerTranslations(context.locale, [
			"common",
			"countries",
			"languages",
			"help",
		])),
	}

	return {
		props,
	}
}

export default FAQs
